import { useScroll } from "@react-three/drei"
import { useEffect, useRef } from "react"
import { useFrame } from '@react-three/fiber'

import normalizeWheel from "normalize-wheel-es"

export function TouchScrollLimiter({ factor = 0.3, smooth = 0.12, enabled = true }) {
    const scroll = useScroll()
    const target = useRef(0)
    const startY = useRef(0)
    const startTop = useRef(0)

    useEffect(() => {
        if (!enabled) return
        const el = scroll.el
        if (!el) return

        el.style.overscrollBehavior = 'contain'
        el.style.touchAction = 'none'

        const onTouchStart = (e) => {
            startY.current = e.touches[0].clientY
            startTop.current = el.scrollTop
            target.current = el.scrollTop
            ensureRaf()
        }

        const onTouchMove = (e) => {
            e.preventDefault()
            const y = e.touches[0].clientY
            const dy = startY.current - y
            target.current = startTop.current + dy * factor
            ensureRaf()
        }

        let rafId = 0
        let running = false

        const raf = () => {
            const diff = target.current - el.scrollTop
            if (Math.abs(diff) < 0.25) {
                running = false
                rafId = 0
                return
            }
            el.scrollTop += diff * smooth
            rafId = requestAnimationFrame(raf)
        }

        const ensureRaf = () => {
            if (running) return
            running = true
            rafId = requestAnimationFrame(raf)
        }

        el.addEventListener('touchstart', onTouchStart, { passive: true })
        el.addEventListener('touchmove', onTouchMove, { passive: false })

        return () => {
            cancelAnimationFrame(rafId)
            el.removeEventListener('touchstart', onTouchStart)
            el.removeEventListener('touchmove', onTouchMove)
        }
    }, [scroll, factor, smooth, enabled])

    return null
}

export function DesktopScrollLimiter({
    factor = 0.5,
    smooth = 0.08,
    maxDeltaPerEvent = 140,
    maxDeltaPerFrame = 90,
    enabled = true,
}) {
    const scroll = useScroll()
    const target = useRef(0)
    const pending = useRef(0)
    const ready = useRef(false)

    useEffect(() => {
        console.log('[Limiter]', {
            enabled,
            anyPointerFine: window.matchMedia('(any-pointer: fine)').matches,
            coarseOnly: !window.matchMedia('(any-pointer: fine)').matches && window.matchMedia('(pointer: coarse)').matches,
            hasScrollEl: !!scroll?.el,
        })

        if (!enabled) return

        // Skip uniquement sur vrai mobile/tablette pure
        const isCoarseOnly =
            !window.matchMedia('(any-pointer: fine)').matches &&
            window.matchMedia('(pointer: coarse)').matches
        if (isCoarseOnly) return

        const el = scroll?.el
        if (!el) return

        target.current = el.scrollTop
        el.style.overscrollBehavior = 'contain'
        ready.current = true

        const onWheel = (e) => {
            e.preventDefault()
            e.stopPropagation()

            const n = normalizeWheel(e)
            let delta = n.pixelY

            delta = Math.max(-maxDeltaPerEvent, Math.min(maxDeltaPerEvent, delta))
            pending.current += delta * factor
        }

        el.addEventListener('wheel', onWheel, { passive: false, capture: true })

        return () => {
            el.removeEventListener('wheel', onWheel, { capture: true })
            ready.current = false
            pending.current = 0
        }
    }, [scroll?.el, enabled, factor, maxDeltaPerEvent])

    useFrame(() => {
        if (!ready.current) return
        const el = scroll?.el
        if (!el) return

        const maxScroll = el.scrollHeight - el.clientHeight

        let step = pending.current
        if (step > maxDeltaPerFrame) step = maxDeltaPerFrame
        if (step < -maxDeltaPerFrame) step = -maxDeltaPerFrame
        pending.current -= step

        target.current = Math.max(0, Math.min(maxScroll, target.current + step))
        el.scrollTop += (target.current - el.scrollTop) * smooth
    })

    return null
}
